import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaBed,
  FaBath,
  FaFilter,
  FaMapMarkerAlt,
  FaStar,
  FaWifi,
  FaParking,
  FaSnowflake,
} from 'react-icons/fa';
import { getPublicBnbUnits, getPublicProperties } from '../../api/services/propertyService';
import { buildPropertyDetailsSearch } from '../../utils/publicListingScope';
import API_BASE_URL from '../../api/config/apiConfig';
import PublicSiteHeader from '../../components/PublicSiteHeader/PublicSiteHeader';
import './PublicListings.css';

const parseRatingValue = (row) => {
  const raw = row?.avgRating ?? row?.averageRating ?? row?.averageScore ?? row?.rating ?? 0;
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
};

const parseRatingCount = (row) => {
  const raw = row?.ratingCount ?? row?.totalRatings ?? row?.reviewsCount ?? 0;
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
};

const normalizeImage = (rawPath, fallback) => {
  if (!rawPath) return fallback;
  return rawPath.startsWith('/') ? `${API_BASE_URL}${rawPath}` : rawPath;
};

const PublicListings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const requestedType = query.get('type');
  const initialType = requestedType === 'bnb' || requestedType === 'buy' ? requestedType : 'rentals';
  const forceGuestView = location?.state?.asGuest === true || query.get('guest') === '1';

  const [listingType, setListingType] = useState(initialType);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rentals, setRentals] = useState([]);
  const [bnbs, setBnbs] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({ search: '', minPrice: '', maxPrice: '', minBedrooms: '', minBathrooms: '' });

  const loadData = useCallback(async (type, filters) => {
    setLoading(true);
    setError('');
    try {
      if (type === 'bnb') {
        const bnbRes = await getPublicBnbUnits({
          search: filters.search,
          minPrice: filters.minPrice,
          maxPrice: filters.maxPrice,
        });
        const bnbRows = Array.isArray(bnbRes?.data) ? bnbRes.data : (bnbRes?.data?.content || []);
        setBnbs(bnbRows);
        setRentals([]);
      } else if (type === 'buy') {
        const rentalRes = await getPublicProperties({
          page: 0, size: 80, forSale: true,
          search: filters.search,
          minPrice: filters.minPrice,
          maxPrice: filters.maxPrice,
          minBedrooms: filters.minBedrooms,
          minBathrooms: filters.minBathrooms,
        });
        const rentalPayload = rentalRes?.data || rentalRes;
        const rentalRows = Array.isArray(rentalPayload?.content) ? rentalPayload.content : [];
        setRentals(rentalRows);
        setBnbs([]);
      } else {
        // rentals — fetch both in parallel so tab switching is instant
        const [bnbRes, rentalRes] = await Promise.all([
          getPublicBnbUnits({ search: filters.search, minPrice: filters.minPrice, maxPrice: filters.maxPrice }),
          getPublicProperties({
            page: 0, size: 80,
            search: filters.search,
            minPrice: filters.minPrice,
            maxPrice: filters.maxPrice,
            minBedrooms: filters.minBedrooms,
            minBathrooms: filters.minBathrooms,
          }),
        ]);
        const bnbRows = Array.isArray(bnbRes?.data) ? bnbRes.data : (bnbRes?.data?.content || []);
        const rentalPayload = rentalRes?.data || rentalRes;
        const rentalRows = Array.isArray(rentalPayload?.content) ? rentalPayload.content : [];
        setBnbs(bnbRows);
        setRentals(rentalRows);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load listings');
      setRentals([]);
      setBnbs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(listingType, appliedFilters);
  }, [listingType, appliedFilters, loadData]);

  const handleApplyFilters = (newFilters) => {
    setAppliedFilters(newFilters);
  };

  const [filters, setFilters] = useState({
    search: '',
    minRating: '',
    minPrice: '',
    maxPrice: '',
    minBedrooms: '',
    minBathrooms: '',
    minNightPrice: '',
    maxNightPrice: '',
  });
  const [sortBy, setSortBy] = useState('recommended');
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [isMobileSortOpen, setIsMobileSortOpen] = useState(false);

  useEffect(() => {
    const modalOpen = isMobileFiltersOpen || isMobileSortOpen;
    if (!modalOpen) return undefined;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.classList.add('pl-modal-open');
    document.body.style.top = `-${scrollY}px`;

    return () => {
      const savedTop = document.body.style.top;
      document.body.classList.remove('pl-modal-open');
      document.body.style.top = '';
      const restoreY = savedTop ? Math.abs(parseInt(savedTop, 10)) : 0;
      window.scrollTo(0, restoreY);
    };
  }, [isMobileFiltersOpen, isMobileSortOpen]);

  // Map raw API rows into the card shape expected by the render code.
  const sourceRows = useMemo(() => {
    if (listingType === 'bnb') {
      return (bnbs || []).map((row, idx) => {
        const photos = Array.isArray(row?.photos) ? row.photos.filter(Boolean) : [];
        const amenities = Array.isArray(row?.amenities) ? row.amenities : Array.isArray(row?.unitAmenities) ? row.unitAmenities : [];
        return {
          id: `${row?.propertyId || row?.propertyID || 'bnb'}-${idx}`,
          propertyId: row?.propertyId || row?.propertyID,
          unitId: row?.unitId || row?.unitID,
          title: row?.propertyName || row?.unitName || 'BnB listing',
          location: row?.propertyAddress || 'Location not specified',
          image: normalizeImage(photos[0], 'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&q=80&w=900'),
          priceNumber: Number(row?.pricePerNight || 0),
          priceText: `KES ${Number(row?.pricePerNight || 0).toLocaleString()} / night`,
          avgRating: parseRatingValue(row),
          ratingCount: parseRatingCount(row),
          amenities,
          available: row?.isAvailableForDates !== false,
        };
      });
    }
    // rentals + buy share the same property shape
    return (rentals || []).map((prop, idx) => {
      const area = prop?.location?.area || '';
      const city = prop?.location?.city || '';
      const locStr = [area, city].filter(Boolean).join(', ') || prop?.address || 'Location not specified';
      const cover = Array.isArray(prop?.imageUrls) && prop.imageUrls.length > 0 ? prop.imageUrls[0] : prop?.imageUrl;
      const units = Array.isArray(prop?.units) ? prop.units : [];
      const bedrooms = units.length > 0 ? Math.max(...units.map((u) => Number(u?.bedrooms || 0))) : Number(prop?.bedrooms || 0);
      const bathrooms = units.length > 0 ? Math.max(...units.map((u) => Number(u?.bathrooms || 0))) : Number(prop?.bathrooms || 0);
      let rentRaw = prop?.lowestRent || prop?.rent || prop?.price || 0;
      if ((!rentRaw || Number(rentRaw) === 0) && units.length > 0) {
        const unitRents = units.map((u) => Number(u?.monthlyRent || u?.rent || 0)).filter((v) => Number.isFinite(v) && v > 0);
        if (unitRents.length > 0) rentRaw = Math.min(...unitRents);
      }
      const buyPrice = listingType === 'buy' ? Number(prop?.salePrice || prop?.sellingPrice || prop?.askingPrice || prop?.price || 0) : 0;
      const displayPrice = listingType === 'buy' ? buyPrice : Number(rentRaw || 0);
      const priceText = displayPrice > 0
        ? (listingType === 'buy' ? `KES ${displayPrice.toLocaleString()}` : `KES ${displayPrice.toLocaleString()} / month`)
        : 'Price on request';
      return {
        id: `${prop?.id || 'prop'}-${idx}`,
        propertyId: prop?.id,
        title: prop?.name || (listingType === 'buy' ? 'Property for Sale' : 'Rental property'),
        location: locStr,
        image: normalizeImage(cover, 'https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&q=80&w=900'),
        priceNumber: displayPrice,
        priceText,
        avgRating: parseRatingValue(prop),
        ratingCount: parseRatingCount(prop),
        amenities: Array.isArray(prop?.amenities) ? prop.amenities : [],
        bedrooms,
        bathrooms,
      };
    });
  }, [bnbs, rentals, listingType]);

  // Sort only — filtering is now server-side. Rating filter stays client-side (not a backend param).
  const filteredRows = useMemo(() => {
    const minRating = Number(filters.minRating || 0);
    let rows = sourceRows;
    if (minRating > 0) {
      rows = rows.filter((item) => Number(item.avgRating || 0) >= minRating);
    }
    return [...rows].sort((a, b) => {
      if (sortBy === 'price_low') return (a.priceNumber || 0) - (b.priceNumber || 0);
      if (sortBy === 'price_high') return (b.priceNumber || 0) - (a.priceNumber || 0);
      if (sortBy === 'rating_high') return (b.avgRating || 0) - (a.avgRating || 0);
      return (b.avgRating || 0) - (a.avgRating || 0);
    });
  }, [filters.minRating, sortBy, sourceRows]);

  const clearFilters = () => {
    setFilters({
      search: '',
      minRating: '',
      minPrice: '',
      maxPrice: '',
      minBedrooms: '',
      minBathrooms: '',
      minNightPrice: '',
      maxNightPrice: '',
    });
    setAppliedFilters({ search: '', minPrice: '', maxPrice: '', minBedrooms: '', minBathrooms: '' });
  };

  const applyFilters = () => {
    const serverFilters = listingType === 'bnb'
      ? { search: filters.search, minPrice: filters.minNightPrice, maxPrice: filters.maxNightPrice }
      : { search: filters.search, minPrice: filters.minPrice, maxPrice: filters.maxPrice, minBedrooms: filters.minBedrooms, minBathrooms: filters.minBathrooms };
    handleApplyFilters(serverFilters);
  };

  const openMapSearch = () => {
    const queryText = filters.search?.trim() || 'Rental properties in Kenya';
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(queryText)}`, '_blank');
  };

  const renderFiltersForm = () => (
    <>
      <label>Search</label>
      <input
        type="text"
        placeholder="Area or property name"
        value={filters.search}
        onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
      />

      <label>Minimum rating</label>
      <select
        value={filters.minRating}
        onChange={(e) => setFilters((prev) => ({ ...prev, minRating: e.target.value }))}
      >
        <option value="">Any rating</option>
        <option value="3.5">3.5+</option>
        <option value="4">4.0+</option>
        <option value="4.5">4.5+</option>
      </select>

      {listingType !== 'bnb' ? (
        <>
          <label>{listingType === 'buy' ? 'Min property price (KES)' : 'Min monthly price (KES)'}</label>
          <input
            type="number"
            placeholder="e.g. 15000"
            value={filters.minPrice}
            onChange={(e) => setFilters((prev) => ({ ...prev, minPrice: e.target.value }))}
          />
          <label>{listingType === 'buy' ? 'Max property price (KES)' : 'Max monthly price (KES)'}</label>
          <input
            type="number"
            placeholder="e.g. 80000"
            value={filters.maxPrice}
            onChange={(e) => setFilters((prev) => ({ ...prev, maxPrice: e.target.value }))}
          />
          <label>Minimum bedrooms</label>
          <select
            value={filters.minBedrooms}
            onChange={(e) => setFilters((prev) => ({ ...prev, minBedrooms: e.target.value }))}
          >
            <option value="">Any</option>
            <option value="1">1+</option>
            <option value="2">2+</option>
            <option value="3">3+</option>
          </select>
          <label>Minimum bathrooms</label>
          <select
            value={filters.minBathrooms}
            onChange={(e) => setFilters((prev) => ({ ...prev, minBathrooms: e.target.value }))}
          >
            <option value="">Any</option>
            <option value="1">1+</option>
            <option value="2">2+</option>
            <option value="3">3+</option>
          </select>
        </>
      ) : (
        <>
          <label>Min night price (KES)</label>
          <input
            type="number"
            placeholder="e.g. 3000"
            value={filters.minNightPrice}
            onChange={(e) => setFilters((prev) => ({ ...prev, minNightPrice: e.target.value }))}
          />
          <label>Max night price (KES)</label>
          <input
            type="number"
            placeholder="e.g. 15000"
            value={filters.maxNightPrice}
            onChange={(e) => setFilters((prev) => ({ ...prev, maxNightPrice: e.target.value }))}
          />
        </>
      )}

      <button type="button" className="clear-filters-btn" onClick={clearFilters}>Clear filters</button>
      <button type="button" className="apply-filters-btn" onClick={applyFilters}>Apply filters</button>
    </>
  );

  return (
    <>
    <div className="public-listings-page">
      <PublicSiteHeader forceGuestView={forceGuestView} />

      <div className="public-listings-type-toggle">
        <button
          type="button"
          className={listingType === 'rentals' ? 'active' : ''}
          onClick={() => setListingType('rentals')}
        >
          Rentals
        </button>
        <button
          type="button"
          className={listingType === 'bnb' ? 'active' : ''}
          onClick={() => setListingType('bnb')}
        >
          Short BnB Stays
        </button>
        <button
          type="button"
          className={listingType === 'buy' ? 'active' : ''}
          onClick={() => setListingType('buy')}
        >
          Buy Property
        </button>
      </div>

      <div className="public-listings-mobile-actions">
        <button type="button" onClick={() => setIsMobileSortOpen(true)}>Sort</button>
        <button type="button" onClick={() => setIsMobileFiltersOpen(true)}><FaFilter /> Filter</button>
        <button type="button" onClick={openMapSearch}>Map</button>
      </div>

      <div className="public-listings-layout">
        <aside className="public-filters-panel">
          <div className="filter-title"><FaFilter /> Filters</div>

          {loading ? (
            <div className="pl-skeleton-panel" aria-label="Loading filters">
              <div className="pl-skel-label pl-shimmer" />
              <div className="pl-skel-input pl-shimmer" />
              <div className="pl-skel-label pl-shimmer" />
              <div className="pl-skel-input pl-shimmer" />
              <div className="pl-skel-label pl-shimmer" />
              <div className="pl-skel-input pl-shimmer" />
              <div className="pl-skel-label pl-shimmer" />
              <div className="pl-skel-input pl-shimmer" />
              <div className="pl-skel-btn pl-shimmer" />
            </div>
          ) : (
            renderFiltersForm()
          )}
        </aside>

        <section className="public-results-panel">
          <div className="public-results-head">
            {loading ? <div className="pl-skel-head pl-shimmer" /> : <p>{`${filteredRows.length} properties found`}</p>}
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} disabled={loading}>
              <option value="recommended">Sort: Recommended</option>
              <option value="rating_high">Rating high to low</option>
              <option value="price_low">Price low to high</option>
              <option value="price_high">Price high to low</option>
            </select>
          </div>

          {error && <div className="public-results-empty">{error}</div>}

          {!error && !loading && filteredRows.length === 0 && (
            <div className="public-results-empty">No listings match your filters.</div>
          )}

          <div className="public-results-list">
            {loading && !error ? (
              Array.from({ length: 6 }).map((_, idx) => (
                <article key={`skeleton-${idx}`} className="public-listing-card skeleton" aria-hidden="true">
                  <div className="public-listing-image-col">
                    <div className="pl-skel-img pl-shimmer" />
                  </div>
                  <div className="public-listing-main-col">
                    <div className="pl-skel-line title pl-shimmer" />
                    <div className="pl-skel-line pl-shimmer" />
                    <div className="pl-skel-line short pl-shimmer" />
                    <div className="pl-skel-pills">
                      <span className="pl-skel-pill pl-shimmer" />
                      <span className="pl-skel-pill pl-shimmer" />
                      <span className="pl-skel-pill pl-shimmer" />
                    </div>
                  </div>
                  <div className="public-listing-side-col">
                    <div className="pl-skel-badge pl-shimmer" />
                    <div className="pl-skel-line tiny pl-shimmer" />
                    <div className="pl-skel-line price pl-shimmer" />
                    <div className="pl-skel-cta pl-shimmer" />
                  </div>
                </article>
              ))
            ) : (
              filteredRows.map((item) => (
              <article key={item.id} className="public-listing-card">
                <div className="public-listing-image-col">
                  <img src={item.image} alt={item.title} />
                </div>

                <div className="public-listing-main-col">
                  <h3>{item.title}</h3>
                  <p className="listing-location"><FaMapMarkerAlt /> {item.location}</p>

                  {listingType !== 'bnb' && (
                    <div className="listing-stats">
                      <span><FaBed /> {item.bedrooms || 0} bd</span>
                      <span><FaBath /> {item.bathrooms || 0} ba</span>
                    </div>
                  )}

                  <div className="listing-amenities">
                    {(item.amenities || []).slice(0, 4).map((amenity) => (
                      <span key={amenity}>{amenity.replace(/_/g, ' ')}</span>
                    ))}
                    {(!item.amenities || item.amenities.length === 0) && (
                      <>
                        <span><FaWifi /> Wifi</span>
                        <span><FaParking /> Parking</span>
                        <span><FaSnowflake /> Furnished</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="public-listing-side-col">
                  <div className="listing-rating-badge">
                    <FaStar /> {Number(item.avgRating || 0).toFixed(1)}
                  </div>
                  <small>{Number(item.ratingCount || 0)} reviews</small>
                  <strong>{item.priceText}</strong>
                  <button
                    type="button"
                    onClick={() => {
                      const search = buildPropertyDetailsSearch({
                        unitId: item.unitId,
                        scope: listingType === 'bnb' ? 'BNB' : listingType === 'rentals' ? 'RENTAL' : undefined,
                      });
                      navigate(`/property/${item.propertyId}${search ? `?${search}` : ''}`);
                    }}
                  >
                    View details
                  </button>
                </div>
              </article>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
    {isMobileFiltersOpen && (
      <div className="pl-mobile-sheet-overlay" onClick={() => setIsMobileFiltersOpen(false)}>
        <aside className="pl-mobile-sheet" onClick={(e) => e.stopPropagation()}>
          <div className="pl-mobile-sheet-head">
            <button type="button" className="pl-mobile-sheet-close" onClick={() => setIsMobileFiltersOpen(false)}>×</button>
            <h2>Filters</h2>
            <button type="button" className="pl-mobile-sheet-clear" onClick={clearFilters}>Clear</button>
          </div>
          <div className="pl-mobile-sheet-body">
            {renderFiltersForm()}
          </div>
        </aside>
      </div>
    )}
    {isMobileSortOpen && (
      <div className="pl-mobile-sheet-overlay" onClick={() => setIsMobileSortOpen(false)}>
        <aside className="pl-mobile-sheet pl-mobile-sort-sheet" onClick={(e) => e.stopPropagation()}>
          <div className="pl-mobile-sheet-head">
            <button type="button" className="pl-mobile-sheet-close" onClick={() => setIsMobileSortOpen(false)}>×</button>
            <h2>Sort</h2>
            <span />
          </div>
          <div className="pl-mobile-sort-options">
            <button type="button" className={sortBy === 'recommended' ? 'active' : ''} onClick={() => { setSortBy('recommended'); setIsMobileSortOpen(false); }}>Recommended</button>
            <button type="button" className={sortBy === 'rating_high' ? 'active' : ''} onClick={() => { setSortBy('rating_high'); setIsMobileSortOpen(false); }}>Rating high to low</button>
            <button type="button" className={sortBy === 'price_low' ? 'active' : ''} onClick={() => { setSortBy('price_low'); setIsMobileSortOpen(false); }}>Price low to high</button>
            <button type="button" className={sortBy === 'price_high' ? 'active' : ''} onClick={() => { setSortBy('price_high'); setIsMobileSortOpen(false); }}>Price high to low</button>
          </div>
        </aside>
      </div>
    )}
    </>
  );
};

export default PublicListings;
